import React, { useState } from 'react';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';
import type { PiletaTipo } from '../../types';
import { piletaTipoFormFields } from '../../config/formFields';
import { getBytes, ref as storageRef, uploadBytes } from 'firebase/storage';
import { storage } from '../../firebase';

interface TablasSectionProps {
  data: {
    tiposPiletas: PiletaTipo[];
    [key: string]: any;
  };
  operations: {
    handlePiletaTipoSubmit: (data: Record<string, any>) => Promise<void>;
    handlePiletaTipoDelete: (item: PiletaTipo) => Promise<void>;
    handlePiletaTipoEdit: (item: PiletaTipo, key: string, newValue: any) => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const TablasSection: React.FC<TablasSectionProps> = ({ data, operations, userPermissions }) => {
  const [editingTipo, setEditingTipo] = useState<PiletaTipo | null>(null);
  const [csvContent, setCsvContent] = useState('');
  const [csvError, setCsvError] = useState<string | null>(null);
  const [isLoadingCsv, setIsLoadingCsv] = useState(false);
  const [isUploadingCsv, setIsUploadingCsv] = useState(false);

  const normalizedTableName = (value: string) => value.trim().replace(/\.csv$/i, '');

  const validateCsv = (content: string): { rows: number; maxLevel: number } => {
    const lines = content.replace(/\r/g, '').split('\n').filter(line => line.trim() !== '');
    if (lines.length < 2) throw new Error('El CSV debe incluir encabezado y al menos una equivalencia.');

    const header = lines[0].split(',').map(value => value.trim().toLowerCase());
    if (header.length < 2 || header[0] !== 'nivel' || header[1] !== 'volumen') {
      throw new Error('El encabezado debe ser exactamente: nivel,volumen');
    }

    let previousLevel = -Infinity;
    for (let index = 1; index < lines.length; index++) {
      const parts = lines[index].split(',').map(value => value.trim());
      const level = Number(parts[0]);
      const volume = Number(parts[1]);
      if (parts.length < 2 || !Number.isFinite(level) || !Number.isFinite(volume)) {
        throw new Error(`Fila ${index + 1}: nivel o volumen inválido.`);
      }
      if (level <= previousLevel) {
        throw new Error(`Fila ${index + 1}: los niveles deben ser únicos y estar ordenados de menor a mayor.`);
      }
      if (level < 0 || volume < 0) {
        throw new Error(`Fila ${index + 1}: no se permiten valores negativos.`);
      }
      previousLevel = level;
    }

    return { rows: lines.length - 1, maxLevel: previousLevel };
  };

  const openCsvEditor = async (tipo: PiletaTipo) => {
    const tableName = normalizedTableName(tipo.tabla || '');
    if (!tableName) {
      setEditingTipo(tipo);
      setCsvContent('nivel,volumen\n');
      setCsvError('Asigná un nombre de tabla antes de subir el CSV.');
      return;
    }

    setEditingTipo(tipo);
    setCsvError(null);
    setIsLoadingCsv(true);
    try {
      const bytes = await getBytes(storageRef(storage, `tablas/${tableName}.csv`), 5 * 1024 * 1024);
      setCsvContent(new TextDecoder('utf-8').decode(bytes));
    } catch (error: any) {
      if (error?.code === 'storage/object-not-found') {
        setCsvContent('nivel,volumen\n');
        setCsvError('La tabla todavía no existe en Storage. Podés crearla pegando o seleccionando un CSV.');
      } else {
        setCsvError(`No se pudo descargar la tabla actual: ${error?.message || error}`);
      }
    } finally {
      setIsLoadingCsv(false);
    }
  };

  const loadLocalCsv = async (file: File | undefined) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setCsvError('Seleccioná un archivo con extensión .csv.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setCsvError('El archivo supera el límite de 5 MB.');
      return;
    }
    const content = await file.text();
    try {
      validateCsv(content);
      setCsvContent(content);
      setCsvError(null);
    } catch (error: any) {
      setCsvError(error.message);
    }
  };

  const uploadCurrentCsv = async () => {
    if (!editingTipo) return;
    const tableName = normalizedTableName(editingTipo.tabla || '');
    if (!tableName) {
      setCsvError('El tipo no tiene un nombre de tabla válido.');
      return;
    }

    try {
      const summary = validateCsv(csvContent);
      setIsUploadingCsv(true);
      setCsvError(null);
      await uploadBytes(
        storageRef(storage, `tablas/${tableName}.csv`),
        new Blob([csvContent.replace(/\r/g, '')], { type: 'text/csv;charset=utf-8' }),
        { contentType: 'text/csv', customMetadata: { piletaTipoId: editingTipo.id } }
      );
      // Keep Firestore canonical (without .csv); CRUDHelper also updates updatedAt.
      await operations.handlePiletaTipoEdit(editingTipo, 'tabla', tableName);
      alert(`✅ Tabla actualizada: ${summary.rows} equivalencias, altura máxima ${summary.maxLevel} cm. Las apps detectarán la nueva fecha de Storage.`);
      setEditingTipo(null);
    } catch (error: any) {
      setCsvError(error?.message || String(error));
    } finally {
      setIsUploadingCsv(false);
    }
  };
  // Table column configurations for PiletaTipo
  const piletaTipoColumns: TableColumn<PiletaTipo>[] = [
    {
      key: 'nombre',
      title: 'Nombre del Tipo',
      width: '30%',
      editable: true,
      editType: 'text',
      onEdit: operations.handlePiletaTipoEdit
    },
    {
      key: 'tabla',
      title: 'Tabla de Volumen',
      width: '50%',
      editable: true,
      editType: 'text',
      onEdit: operations.handlePiletaTipoEdit,
      render: (value) => (
        <span style={{
          fontFamily: 'monospace',
          fontSize: '13px',
          padding: '4px 8px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #dee2e6'
        }}>
          {value || 'Sin tabla asignada'}
        </span>
      )
    },
    {
      key: 'id',
      title: 'ID',
      width: '20%',
      render: (value) => (
        <span style={{
          fontFamily: 'monospace',
          fontSize: '11px',
          color: '#6c757d'
        }}>
          {value}
        </span>
      )
    }
  ];

  // Quick Setup Handler
  const handlePiletaTiposQuickSetup = async () => {
    if (window.confirm('¿Cargar tipos de piletas estándar? Esta acción creará tipos de piletas comunes en operaciones de control de sólidos.')) {
      const tipoTemplates = [
        { nombre: 'Pileta Activa', tabla: 'tabla_pileta_activa.csv' },
        { nombre: 'Pileta de Reserva', tabla: 'tabla_pileta_reserva.csv' },
        { nombre: 'Pileta de Succión', tabla: 'tabla_pileta_succion.csv' },
        { nombre: 'Pileta de Descarga', tabla: 'tabla_pileta_descarga.csv' },
        { nombre: 'Pileta de Mezcla', tabla: 'tabla_pileta_mezcla.csv' },
        { nombre: 'Pileta de Sedimentación', tabla: 'tabla_pileta_sedimentacion.csv' }
      ];

      try {
        for (const template of tipoTemplates) {
          await operations.handlePiletaTipoSubmit(template);
        }
        alert('✅ Tipos de piletas estándar cargados exitosamente');
      } catch (error) {
        console.error('Error cargando tipos de piletas:', error);
        alert('❌ Error cargando tipos de piletas. Ver consola para detalles.');
      }
    }
  };

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>📊 Gestión de Tablas</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Administra los tipos de piletas y sus tablas de volúmenes asociadas
        </p>
      </div>

      {/* Pileta Tipos Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Tipos de Piletas',
          '📊',
          data.tiposPiletas,
          piletaTipoFormFields,
          piletaTipoColumns,
          operations.handlePiletaTipoSubmit,
          async () => {}, // PiletaTipos don't have toggle functionality
          operations.handlePiletaTipoDelete,
          {
            description: 'Define los tipos de piletas disponibles con sus tablas de volúmenes asociadas para cálculos precisos',
            customActions: [
              {
                label: 'Editar/subir CSV',
                icon: '📤',
                variant: 'primary',
                onClick: async (tipo: PiletaTipo) => openCsvEditor(tipo)
              }
            ],
            quickSetup: {
              title: 'Carga Rápida de Tipos',
              description: 'Crea tipos de piletas estándar (Activa, Reserva, Succión, Descarga, Mezcla, Sedimentación)',
              buttonLabel: '📊 Cargar Tipos Estándar',
              onQuickSetup: handlePiletaTiposQuickSetup
            }
          }
        )}
      />

      {editingTipo && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="csv-editor-title"
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', background: 'rgba(0, 0, 0, 0.65)'
          }}
        >
          <div style={{ width: 'min(900px, 100%)', maxHeight: '92vh', overflowY: 'auto', padding: '24px', border: '2px solid #2196F3', borderRadius: '12px', background: '#f7fbff', boxShadow: '0 16px 48px rgba(0,0,0,.35)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start' }}>
              <h3 id="csv-editor-title" style={{ marginTop: 0 }}>Editar tabla: {editingTipo.nombre}</h3>
              <button onClick={() => setEditingTipo(null)} disabled={isUploadingCsv} aria-label="Cerrar editor">✕</button>
            </div>
            <p style={{ margin: '8px 0', color: '#555' }}>
              Archivo remoto: <code>tablas/{normalizedTableName(editingTipo.tabla)}.csv</code>. Podés modificar sólo las filas necesarias o extender la tabla con niveles nuevos.
            </p>
            <input type="file" accept=".csv,text/csv" onChange={event => loadLocalCsv(event.target.files?.[0])} disabled={isUploadingCsv} />
            {isLoadingCsv ? <p style={{ padding: '24px 0', fontWeight: 600 }}>Cargando tabla actual desde Firebase…</p> : (
              <textarea
                value={csvContent}
                onChange={event => setCsvContent(event.target.value)}
                spellCheck={false}
                rows={18}
                style={{ width: '100%', marginTop: '12px', fontFamily: 'monospace', fontSize: '13px', padding: '12px', boxSizing: 'border-box' }}
              />
            )}
            {csvError && <p style={{ color: '#c62828', fontWeight: 600 }}>{csvError}</p>}
            {!userPermissions.canEditReports && <p style={{ color: '#c62828' }}>Tu usuario no tiene permiso para reemplazar tablas.</p>}
            <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
              <button onClick={uploadCurrentCsv} disabled={isLoadingCsv || isUploadingCsv || !userPermissions.canEditReports}>
                {isUploadingCsv ? 'Subiendo…' : 'Validar y reemplazar tabla'}
              </button>
              <button onClick={() => setEditingTipo(null)} disabled={isUploadingCsv}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* System Information Section */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#e8f5e8',
        borderRadius: '12px',
        border: '2px solid #4CAF50'
      }}>
        <h3 style={{ color: '#2E7D32', marginBottom: '15px' }}>📊 Sistema de Tablas de Volúmenes</h3>

        {/* Statistics */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '20px'
        }}>
          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              📊 Tipos de Piletas
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#4CAF50'
            }}>
              {data.tiposPiletas.length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🛢️ Con Tabla Asignada
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#2196F3'
            }}>
              {data.tiposPiletas.filter(tipo => tipo.tabla && tipo.tabla.trim() !== '').length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              ⚠️ Sin Tabla
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#FF9800'
            }}>
              {data.tiposPiletas.filter(tipo => !tipo.tabla || tipo.tabla.trim() === '').length}
            </div>
          </div>
        </div>

        {/* System Description */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h4 style={{ color: '#2E7D32', marginBottom: '15px' }}>🔧 Funcionalidad del Sistema</h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '15px',
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            <div>
              <strong>📊 Cálculo de Volúmenes:</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                <li>Interpolación por nivel de pileta</li>
                <li>Capacidades según tipo y dimensiones</li>
                <li>Corrección por inclinación y forma</li>
              </ul>
            </div>
            <div>
              <strong>🔄 Tablas de Datos:</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                <li>Archivos CSV con datos nivel-volumen</li>
                <li>Importación desde Firebase Storage</li>
                <li>Validación de formato y estructura</li>
              </ul>
            </div>
            <div>
              <strong>⚡ Integración del Sistema:</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                <li>Conexión con Control de Piletas</li>
                <li>Sincronización con aplicaciones móviles</li>
                <li>Cálculos en tiempo real</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tipos List */}
        {data.tiposPiletas.length > 0 && (
          <div>
            <h4 style={{ color: '#2E7D32', marginBottom: '10px' }}>📁 Tipos de Piletas Configurados:</h4>
            <div style={{
              backgroundColor: 'white',
              padding: '15px',
              borderRadius: '8px',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {data.tiposPiletas.map((tipo) => (
                <div key={tipo.id} style={{
                  padding: '8px 12px',
                  marginBottom: '4px',
                  backgroundColor: tipo.tabla ? '#e8f5e8' : '#fff3e0',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{
                    fontSize: '16px',
                    color: tipo.tabla ? '#2e7d32' : '#ef6c00'
                  }}>
                    {tipo.tabla ? '📊' : '⚠️'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}>
                      {tipo.nombre}
                    </div>
                    <div style={{
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      color: '#666'
                    }}>
                      {tipo.tabla || 'Sin tabla asignada'}
                    </div>
                  </div>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    backgroundColor: tipo.tabla ? '#e8f5e8' : '#fff3e0',
                    color: tipo.tabla ? '#2e7d32' : '#ef6c00'
                  }}>
                    {tipo.tabla ? 'CONFIGURADO' : 'PENDIENTE'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.tiposPiletas.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
            <h4>No hay tipos de piletas configurados</h4>
            <p>Usa la "Carga Rápida de Tipos" para crear tipos estándar o agrega manualmente los tipos necesarios.</p>
          </div>
        )}

        {/* Instructions */}
        <div style={{
          backgroundColor: '#e3f2fd',
          padding: '15px',
          borderRadius: '8px',
          border: '1px solid #2196f3',
          marginTop: '20px'
        }}>
          <h4 style={{ color: '#1565c0', marginBottom: '10px' }}>💡 Información Importante</h4>
          <p style={{ margin: 0, fontSize: '14px', color: '#1976d2' }}>
            Cada tipo de pileta debe tener una <strong>tabla de volúmenes</strong> asociada para realizar cálculos precisos.
            Las tablas definen la relación entre el nivel del líquido y el volumen contenido en la pileta.
            Esta información es fundamental para el funcionamiento del módulo <strong>🛢️ Control de Piletas</strong>.
          </p>
        </div>
      </div>
    </>
  );
};
